import PropTypes from 'prop-types';
import React from 'react';
import _get from 'lodash/get';
import clsx from 'clsx';
import { useFeatures } from '@atc/features';
import { imageSizes, placeholderImages } from '@atc/images';
import { useDevice } from '@atc/react-device';
import { Carousel } from 'reaxl';
import { InventoryListingV2 } from 'reaxl-listing';
import qs from 'querystring';

/**
 *  Carousel for similar inventory
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function InventoryCarousel(_ref) {
  let {
    carouselSettings = {},
    className,
    clickType = '',
    enableMyWallet = false,
    isCompared = () => {},
    lazyPlaceholder = placeholderImages.imageLoading,
    listings = [],
    listingClassName,
    priceLabel = 'MSRP',
    priceDescription = '',
    onCompareClick = () => {},
    onInventoryListingClick = () => {},
    onListingRemove,
    onListingSelect,
    onMyWalletClick = () => {},
    onNextButtonClick = () => {},
    onPrevButtonClick = () => {},
    onSaveToggle,
    onSettle = () => {},
    onShopAllClick,
    onStaticClick = () => {},
    orientation = 'landscape',
    parentId = '',
    savedListingIds = [],
    showCompareButton = false,
    showFooter = true,
    showListingPaymentRibbon = false,
    uiContext = 'snapshot',
    ...rest
  } = _ref;
  delete rest.onCollapsibleHeaderClick;
  delete rest.listingCTA;
  delete rest.birfParams;
  delete rest.lazyLoad;
  delete rest.onPricingDetailClick;
  delete rest.paymentsCTA;
  delete rest.onViewOwnerInventoryClick;
  delete rest.onTileClick;
  delete rest.pageValue;
  const device = useDevice();
  const isLessThanMd = _get(device, 'lessThan.md', false);
  const {
    inventory_carousel: [enableInventoryCarousel]
  } = useFeatures(['inventory_carousel']);
  const {
    inventoryCarousel,
    inventoryCarouselSm
  } = imageSizes;
  let imgSizes = inventoryCarousel;
  let dynamicMargin = 'margin-right-3';
  if (enableInventoryCarousel) {
    orientation = 'portrait';
    showFooter = false;
    uiContext = 'default';
    imgSizes = inventoryCarouselSm;
    dynamicMargin = isLessThanMd ? 'margin-right-3' : 'margin-right-4';
  }
  const getListings = () => listings.map((listing, index) => {
    const modListing = structuredClone(listing);
    const imgIndex = _get(modListing, 'images.primary', 0);
    modListing.images = modListing.images ? modListing.images : {
      sources: {}
    };
    modListing.images.sources[imgIndex] = {
      ...modListing.images.sources[imgIndex],
      sizes: imgSizes,
      className: 'img-responsive-scale'
    };
    const addOns = {
      ...listing.addOns
    };
    if (!addOns.emailSent) {
      addOns.onListingSelect = onListingSelect;
      addOns.onListingRemove = onListingRemove;
    }
    const onClick = event => onInventoryListingClick(event, listing, index);
    const onSaveToggleClick = onSaveToggle && ((event, data, isSaved) => {
      onSaveToggle(event, data, isSaved, index);
    });

    // Define the listingCTA prop value if the clickType is specified
    // If a clickType query param is specified  - preserve it
    // Vdp url sample: "/cars-for-sale/vehicledetails.xhtml?listingId=558988226&zip=94566"
    const listingCTA = {};
    if (clickType) {
      const vdpUrl = _get(modListing, 'website.href', '');
      const [urlPath = '', urlQuery = ''] = vdpUrl ? vdpUrl.split('?') : [];
      const queryParams = qs.parse(urlQuery);
      queryParams.clickType = queryParams.clickType || clickType;
      listingCTA.href = urlPath ? urlPath + '?' + qs.stringify(queryParams) : '';
    }
    return /*#__PURE__*/_jsx(InventoryListingV2, {
      className: clsx('display-flex flex-column rounded-lg margin-top-4', listingClassName, dynamicMargin),
      onClick: onClick,
      addOns: addOns,
      onSaveToggle: onSaveToggleClick,
      isSaved: savedListingIds.includes(listing.id),
      listing: modListing,
      listingCTA: listingCTA,
      ribbon: listing.ribbon,
      lazyLoad: false,
      loading: "eager",
      onMyWalletClick: onMyWalletClick,
      showFooter: showFooter,
      uiContext: uiContext,
      isCompared: isCompared(modListing),
      onCompareClick: showCompareButton && onCompareClick(modListing),
      clickType: clickType,
      parentId: parentId,
      orientation: orientation,
      priceLabel: priceLabel,
      priceDescription: priceDescription,
      showListingPaymentRibbon: showListingPaymentRibbon
    }, `listing-${listing.id}-${index}`);
  });
  const getCarousel = () => /*#__PURE__*/_jsxs("div", {
    "data-cmp": "inventoryCarousel",
    className: clsx('inventory-carousel', className),
    ...rest,
    children: [/*#__PURE__*/_jsx(Carousel, {
      cellWidth: `${imgSizes.width + (isLessThanMd ? 12 : 16)}px`,
      initialIndex: 0,
      fullHeight: true,
      fullWidth: false,
      onStaticClick: onStaticClick,
      onSettle: onSettle,
      lazyLoad: 1,
      onNextButtonClick: onNextButtonClick,
      onPrevButtonClick: onPrevButtonClick,
      onDragStart: () => {
        document.ontouchmove = e => e.preventDefault();
      },
      onDragEnd: () => {
        document.ontouchmove = () => true;
      },
      ...carouselSettings,
      children: getListings()
    }), rest.children]
  });
  return getCarousel();
}
export default InventoryCarousel;